import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import _ from 'underscore';

import Locator from '../../../../shared/Locator';
import { FREE_PLAN_NAME } from '../../../../shared/subscriptions';
import { Action, ResourceType } from '../../../../typings/permissions';
import ProjectActions from '../../../actions/ProjectActions';
import { window } from '../../../globals';
import { userHasPermissionForProject } from '../../../helpers/permissions';
import type { IAsync, Project, Reference } from '../../../types';
import utils, { containEvent } from '../../../utils';
import Flex from '../../generic/Flex';
import { IconButton } from '../../generic/Button';
import Icon, { icons, IconText } from '../../generic/Icon';
import MultiTabDropdown from '../../generic/MultiTabDropdown';

type ProjectNavigationProps = {
  loading?: boolean;
  project?: IAsync<Project> & { status: { updating?: boolean } };
  reference?: string;
  referenceType?: string;
  selectedRevision?: any; // object or string
  onSelectReference?: Function;
  dropdownOnly?: boolean;
};

type ProjectNavigationState = {
  branch?: string;
  tag?: string;
  selected_ref_type?: string;
  rev_filter?: string;
  dropdownOpen?: boolean;
  showArchivedRefs?: boolean;
};

// Some projects may have a large number of branches, so we limit the number of untracked branches shown in the dropdown
// to prevent performance issues. Users can still search for and select any branch
const MAX_VISIBLE_UNTRACKED_REFS = 100;

class ProjectNavigation extends Component<ProjectNavigationProps, ProjectNavigationState> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  constructor(props: ProjectNavigationProps) {
    super(props);
    // select the ref passed in as prop and set defaults depending on if it's a branch or tag
    const default_ref: Reference = _.find(this.props.project.data?.references, ref => ref.name === this.props.reference);

    this.state = {
      branch: (default_ref && default_ref.type === 'branch') ? default_ref.name : this.props.project.data?.default_branch, // current ref we are BROWSING on in dropdown, the actual branch/tag is passed in as a prop
      tag:
        (default_ref && default_ref.type === 'tag')
          ? default_ref.name
          : ((_.find(this.props.project.data?.references, (ref: Reference) => ref.type === 'tag') || {}) as Reference)
              .name,
      selected_ref_type: (default_ref ? default_ref.type : null) || 'branch',
      rev_filter: '',
      dropdownOpen: false,
      showArchivedRefs: false,
    };
  }

  componentDidMount() {
    window.addEventListener('click', this.handleClick);
  }

  // eslint-disable-next-line @typescript-eslint/naming-convention
  UNSAFE_componentWillReceiveProps(nextProps) {
    // TODO: reload versions if project changes, and switch if refs change
    if (
      nextProps.project.data?.locator !== this.props.project.data?.locator ||
      (nextProps.reference !== this.props.reference && this.state.selected_ref_type)
    ) {
      const setter = {};
      setter[this.state.selected_ref_type] =
        ((_.find(
          nextProps.project.data?.references,
          ref => ref.name === nextProps.reference && ref.type === this.state.selected_ref_type,
        ) || {}) as Reference).name || nextProps.project.data?.default_branch;
      setter.dropdownOpen = false;
      this.setState(setter);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleClick);
  }

  handleClick = _ => {
    if (this.state.dropdownOpen) this.setState({ dropdownOpen: false });
  };

  // switching between branches/tags
  switchDropdown = key => {
    if (key === this.state.selected_ref_type) return;
    this.setState({ selected_ref_type: key });
  };

  trackDefaultBranch = () => {
    const default_branch = this.props.project.data?.default_branch;
    ProjectActions.pushUpdate(this.props.project.data?.locator, {
      tracking_branches: [default_branch],
    });
  };

  onSelectReference = ref => {
    if (this.props.onSelectReference) {
      this.handleClick();
      this.props.onSelectReference(ref);
      return;
    }

    this.context.router.push(
      '/projects/' +
        utils.nuc(this.props.project.data?.locator) +
        '/refs/' +
        ref.type +
        '/' +
        utils.nuc(ref.name) +
        '/' +
        utils.nuc(Locator.parse(ref.revision_id).revision),
    );
  };

  onNavToRefClick = (ref, isTracked, isHidden) => {
    // Unarchive before navigating to the branch.
    if (isHidden) {
      this.onToggleArchived(ref, isTracked, isHidden);
    };
    this.onSelectReference(ref);
  };

  onToggleArchived = (ref, isTracked, isHidden) => {
    if (!utils.isAuthed()) {
      window.showPlanSelectorModal({ tier: FREE_PLAN_NAME });
      return;
    }

    const tracking_branches = this.props.project.data?.tracking_branches || [];
    const hidden_branches = this.props.project.data?.hidden_branches || [];
    if (!isHidden) {
      hidden_branches.push(ref.name);
      if (isTracked) {
        tracking_branches.splice(tracking_branches.indexOf(ref.name), 1);
      }
    } else {
      hidden_branches.splice(hidden_branches.indexOf(ref.name), 1);
    }
    ProjectActions.pushUpdate(this.props.project.data?.locator, {
      tracking_branches,
      hidden_branches,
    });
  };

  onToggleWatched = (ref, isTracked) => {
    if (!utils.isAuthed()) {
      window.showPlanSelectorModal({ tier: FREE_PLAN_NAME });
      return;
    }

    const tracking_branches = this.props.project.data?.tracking_branches || [];
    if (!isTracked) {
      tracking_branches.push(ref.name);
    } else {
      tracking_branches.splice(tracking_branches.indexOf(ref.name), 1);
    }
    ProjectActions.pushUpdate(this.props.project.data?.locator, {
      tracking_branches,
    });
  }

  onRefreshClick = () => {
    const options: any = {};
    if (this.props.selectedRevision && this.props.selectedRevision.locator) {
      // this wont fetch builds not in this revision
      options.revision_id = Locator.parse(this.props.selectedRevision.locator).revision;
    }
    ProjectActions.fetchLatest(this.props.project.data?.locator, false, options);
  };

  onTrackDefaultBranchClick = () => {
    if (!utils.isAuthed()) {
      window.showPlanSelectorModal({ tier: FREE_PLAN_NAME });
      return;
    }
    this.trackDefaultBranch();
  };

  onShowArchivedRefsClick = (ev) => {
    ev.preventDefault();
    this.setState({ showArchivedRefs: true });
  };

  sortReferences = (a, b) => {
    const regex_filter = /[^0-9a-z]/gi;
    const a_name = a.name.replace(regex_filter, '').toLowerCase();
    const b_name = b.name.replace(regex_filter, '').toLowerCase();

    if (a_name > b_name) return 1;
    if (a_name < b_name) return -1;
    return 0;
  };

  _renderRef = (ref, index, arr, cannot_track) => {
    const isSelected = this.props.reference === ref.name;
    const isTracked = (this.props.project?.data?.tracking_branches || []).includes(ref.name);
    const isHidden = (this.props.project?.data?.hidden_branches || []).includes(ref.name);

    let archiveTooltipText;
    if (isHidden) {
      archiveTooltipText = 'Unarchive Branch';
    } else {
      if (isTracked) {
        archiveTooltipText = 'Untrack and Archive';
      } else {
        archiveTooltipText = 'Archive Branch';
      }
    }

    const trackTooltipText = isTracked ? 'Untrack' : 'Track for Release';

    const listItemClassName = classNames('ref-selector-dropdown-item', {
      active: isSelected,
      archived: isHidden,
    });

    const isUserAllowedToEditProject = userHasPermissionForProject(Action.Edit, this.props.project?.data?.locator);
    const shouldShowButtons = !cannot_track && isUserAllowedToEditProject && !isHidden;
    const isDefaultBranch = this.props.project.data?.default_branch === ref.name;

    return (
      <li className={listItemClassName} key={`nav-ref-${ref.name}`}>
        <a href="#" onClick={() => this.onNavToRefClick(ref, isTracked, isHidden)}>
          <Flex justify="space-between">
            <span>
              {ref.name}
              {isDefaultBranch && <small className="margin-left-xs">(default)</small>}
            </span>
            <span className="ref-selector-dropdown-item__buttons">
              <Flex justify="center" align="center" gap="4px">
                {!cannot_track && isUserAllowedToEditProject && (
                  <IconButton
                    id={`archive-${ref.name}`}
                    icon={isHidden ? icons.boxOpen : icons.boxTaped}
                    onClick={containEvent(() => this.onToggleArchived(ref, isTracked, isHidden))}
                    placement="right"
                    tooltip={archiveTooltipText}
                    showTooltip
                  />
                )}
                {shouldShowButtons && (
                  <IconButton
                    id={`tracked-${ref.name}`}
                    icon={isTracked ? icons.eyeSlash : icons.eye}
                    onClick={containEvent(() => this.onToggleWatched(ref, isTracked))}
                    placement="right"
                    tooltip={trackTooltipText}
                    showTooltip
                  />
                )}
              </Flex>
            </span>
          </Flex>
        </a>
      </li>
    );
  };

  // get list of branches/tags. Also filter by user filter
  getBranchOrTagOptions = isTag => {
    const all_refs = _.filter(this.props.project.data?.references, (ref: Reference) => {
      let refInSet = true;
      if (this.state.selected_ref_type) {
        refInSet = ref.name.includes(this.state.selected_ref_type);
      }
      return refInSet && ref.type === (isTag ? 'tag' : 'branch');
    });
    return _.map(all_refs || [], ref => ({
      value: ref.name,
      label: (
        <span className={this.props.project.data?.default_branch === ref.name ? 'inline-header' : ''}>
          {ref.name}
          {this.props.project.data?.tracking_branches.includes(ref.name) && (
            <span className="margin-left-sm">
              <Icon icon={icons.star} />
            </span>
          )}
        </span>
      ),
    }));
  };

  renderRefreshButton = () => (
    <OverlayTrigger
      overlay={
        <Tooltip>
          <b>Last Updated:</b> at least<br />
          {moment(this.props.project.data?.updatedAt).fromNow()}
        </Tooltip>
      }
      placement="top">
      <Button bsStyle="link" disabled={this.props.project.status.updating} onClick={this.onRefreshClick}>
        <IconText
          icon={icons.refresh}
          text="Refresh"
          hasSpin={this.props.project.status.updating}
        />
      </Button>
    </OverlayTrigger>
  )

  renderDropdown = () => {
    let numTags = 0;
    const trackedRefs = [];
    const untrackedRefs = [];
    const archivedRefs = [];
    let visibleTrackedRefs = [];
    let visibleUntrackedRefs = [];
    let visibleArchivedRefs = [];

    const pushIfVisible = (ref, visibleRefCollection) => {
      if (ref.name.includes(this.state.rev_filter)) {
        visibleRefCollection.push(ref);
      }
    };

    _.each(this.props.project.data?.references, (ref: Reference) => {
      if (ref.type === 'tag') numTags++;
      if (ref.type !== this.state.selected_ref_type) {
        return;
      }

      const isTrackedRef = this.state[this.state.selected_ref_type] &&
        this.props.project.data?.tracking_branches.includes(ref.name);
      const isArchivedRef = this.props.project.data?.hidden_branches.includes(ref.name);

      if (isTrackedRef) {
        trackedRefs.push(ref);
        pushIfVisible(ref, visibleTrackedRefs);
      } else if (isArchivedRef) {
        archivedRefs.push(ref);
        pushIfVisible(ref, visibleArchivedRefs);
      } else {
        untrackedRefs.push(ref);
        pushIfVisible(ref, visibleUntrackedRefs);
      }
    });

    visibleTrackedRefs = visibleTrackedRefs.sort(this.sortReferences);
    visibleUntrackedRefs = visibleUntrackedRefs.sort(this.sortReferences);
    visibleArchivedRefs = visibleArchivedRefs.sort(this.sortReferences);

    const hasUntrackedOrArchivedRefs = untrackedRefs.length + archivedRefs.length > 0;
    const hasVisibleUntrackedOrArchivedRefs = visibleUntrackedRefs.length + visibleArchivedRefs.length > 0;

    const tagTagContents = (
      <span>
        <ul className="nav nav-pills nav-stacked" style={{ maxHeight: '300px' }}>
          {visibleUntrackedRefs.length > 0 && (
            _.map(visibleUntrackedRefs, _.partial(this._renderRef, _, _, _, true))
          )}
        </ul>
      </span>
    );

    const branchTabContents = (
      <div>
        <div className="nav-header">Tracked for Release</div>
        <ul className="nav nav-pills nav-stacked" style={{ maxHeight: '25vh' }}>
          {_.map(visibleTrackedRefs, this._renderRef)}
          {trackedRefs.length === 0 ? (
            <li className="nav-empty">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    Tracking a branch will allow FOSSA to listen for changes (Pushes or Pull Requests) on that
                    branch, and trigger new builds within our system.
                  </Tooltip>
                }>
                <h4>
                  <IconText
                    icon={icons.questionCircle}
                    text="No branches being tracked."
                  />
                </h4>
              </OverlayTrigger>
              <Button
                bsStyle={'warning'}
                onClick={this.onTrackDefaultBranchClick}>
                Track '{this.props.project.data?.default_branch}' branch
              </Button>
            </li>
          ) : (visibleTrackedRefs.length === 0 && (
            <li><a>There are no tracked branches</a></li>
          ))}
        </ul>
        {hasUntrackedOrArchivedRefs &&
          <div
            className="nav-header"
            style={{
              borderTop: 'solid 1px rgba(215, 221, 229, 0.9)',
              marginTop: 0,
              paddingTop: 10,
            }}
          >
            Untracked
          </div>}
        <ul className="nav nav-pills nav-stacked" style={{ maxHeight: '25vh' }}>
          { hasUntrackedOrArchivedRefs && !hasVisibleUntrackedOrArchivedRefs &&
            <li><a>There are no untracked branches</a></li>
          }
          {visibleUntrackedRefs.length > 0 &&
            _.map(_.take(visibleUntrackedRefs, MAX_VISIBLE_UNTRACKED_REFS), this._renderRef)}
          { visibleArchivedRefs.length > 0 && (
            this.state.showArchivedRefs || this.state.rev_filter !== ''
              ? _.map(visibleArchivedRefs, this._renderRef)
              : <li className='text-center'><a onClick={this.onShowArchivedRefsClick}><i>Show archived branches...</i></a></li>
          )}
        </ul>
      </div>
    )

    const toggleInnerBody = this.props.dropdownOnly
      ? <a><span>{this.props.reference}</span></a>
      : (
        <div style={{ padding: '5px ' }}>
          <b style={{ opacity: 0.8 }} className="inline-header">{this.props.referenceType}:</b>&nbsp;
          <b>{this.props.reference}</b>&nbsp;
          <br />
          {utils.trimString(this.props.selectedRevision.message || '...', 35)}
        </div>);

    const allTabs = [{
      key: 'branch',
      label: 'Branches',
      contents: branchTabContents,
    }];

    if (numTags > 0) {
      allTabs.push({
        key: 'tag',
        label: 'Tags',
        contents: tagTagContents,
      });
    }

    return (
      <MultiTabDropdown
        id="branch-tag-dropdown"
        open={this.state.dropdownOpen}
        title="Switch Branch / Tag"
        dropdownClassName="project-revision-dropdown"
        dropdownStyle={{
          minWidth: '450px', // required to show the entire Track Branch button for longer branch names
          maxWidth: '80vw', // dyanmic width based on length of longest branch name in list
        }}
        onToggleClick={utils.containEvent(() => {
          this.setState((prevState) => ({ dropdownOpen: !prevState.dropdownOpen }));
        })}
        toggleInnerBody={toggleInnerBody}
        filterEnabled={(this.props.project.data?.references?.length ?? 0) > 15}
        filterPlaceHolderText="Filter by branch/tag"
        filterValue={this.state.rev_filter}
        onFilterChange={(filterVal: string) => this.setState({ rev_filter: filterVal })}
        tabs={allTabs}
        helperTab={
          window.user &&
          window.hasPermission({ action: Action.View, resourceType: ResourceType.ProjectAny }) &&
          this.renderRefreshButton()
        }
        selectedTab={this.state.selected_ref_type}
        onTabClick={(branchOrTag) => this.switchDropdown(branchOrTag)}
      />
    );
  };

  render() {
    if (this.props.loading) return <a>...</a>;
    const dropdown = this.renderDropdown();
    if (this.props.dropdownOnly) {
      return dropdown;
    }
    return (
      <div>
        <div className="project-navigation panel">
          <div>
            <ul className="nav nav-tabs">
              <li className="active">
                <a>History</a>
              </li>
              { userHasPermissionForProject(Action.Edit, this.props.project.data?.locator) && (
                <li className="pull-right">
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        <b>Last Updated:</b> at least<br />
                        {moment(this.props.project.data?.updatedAt).fromNow()}
                      </Tooltip>
                    }
                    placement="top">
                    <Button bsStyle="link" disabled={this.props.project.status.updating} onClick={this.onRefreshClick}>
                      <IconText
                        icon={icons.refresh}
                        text="Refresh"
                        hasSpin={this.props.project.status.updating}
                      />
                    </Button>
                  </OverlayTrigger>
                </li>
              )}
              <li className="pull-right">
                <Button bsStyle="link">
                  {this.props.project.data?.tracking_branches.includes(this.props.reference) ? (
                    <span>
                      <IconText
                        icon={icons.eye}
                        text="Tracked"
                      />
                    </span>
                  ) : (
                    <span>
                      <IconText
                        icon={icons.eyeSlash}
                        text="Untracked"
                      />
                    </span>
                  )}
                </Button>
              </li>
            </ul>
            {dropdown}
          </div>
        </div>
      </div>
    );
  }

}

export default ProjectNavigation;
